import React, { useEffect, useRef, useState } from 'react'
import Text, { TextScale } from 'particles/Text'
import Icon, { IconSize } from 'particles/Icon'
import { StyledRadioButtonContainer, StyledNoSelectedContainer, StyledRadio } from './style'
import { useTranslation } from 'react-i18next'

export type RadioButtonSize = 's' | 'm'

export interface RadioButtonProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
    name?: string
    onChange: React.ChangeEventHandler<HTMLInputElement>
    readOnlyEmptyText?: string
    size?: RadioButtonSize
    selected?: boolean
    isDisabled?: boolean
    label?: string
    isReadOnly?: boolean
    dataTestId?: string
}

export const sizeMap: Record<RadioButtonSize, { iconSize: IconSize; outlineSize: string; textScale: TextScale }> = {
    m: {
        iconSize: '20px',
        outlineSize: '28px',
        textScale: 'bodyM400',
    },
    s: {
        iconSize: '16px',
        outlineSize: '24px',
        textScale: 'bodyS400',
    },
}

const RadioButton: React.FC<RadioButtonProps> = ({
    name,
    onChange,
    readOnlyEmptyText,
    size = 's',
    selected = false,
    isDisabled = false,
    label = '-',
    isReadOnly = false,
    dataTestId,
}) => {
    const [t] = useTranslation()
    const { iconSize, textScale } = sizeMap[size]
    const labelRef = useRef<HTMLDivElement>(null)
    // This will be use to calculate alignItems
    const [offsetHeight, setOffsetHeight] = useState(labelRef.current?.offsetHeight)

    useEffect(() => {
        setOffsetHeight(labelRef.current?.offsetHeight)
    }, [labelRef.current?.offsetHeight])

    return (
        <>
            {isReadOnly ? (
                !selected ? (
                    <StyledNoSelectedContainer size={size}>
                        <Text color="disable" scale={textScale}>
                            {readOnlyEmptyText ? readOnlyEmptyText : t('placeholderText.dropdownMenu.noItem')}
                        </Text>
                    </StyledNoSelectedContainer>
                ) : (
                    <StyledRadioButtonContainer containerHeight={offsetHeight} textScale={textScale} iconSize={size}>
                        <div className="radio-icon-container">
                            <Icon
                                className="radio-icon"
                                name={'bigDot'}
                                form={selected ? 'fill' : 'regular'}
                                color="currentColor"
                                size={iconSize}
                            />
                        </div>
                        <Text ref={labelRef} className="label" scale={textScale}>
                            {label}
                        </Text>
                    </StyledRadioButtonContainer>
                )
            ) : (
                <StyledRadioButtonContainer containerHeight={offsetHeight} textScale={textScale} iconSize={size}>
                    <div className="radio-icon-container">
                        <StyledRadio
                            disableRipple
                            iconSize={size}
                            className="radio"
                            checked={selected}
                            onChange={onChange}
                            name={name}
                            disabled={isDisabled}
                            inputProps={{ 'aria-label': name }}
                            data-testid={dataTestId ? `${dataTestId}-option` : ''}
                        />
                    </div>

                    {label ? (
                        <Text ref={labelRef} className="label" scale={textScale}>
                            {label}
                        </Text>
                    ) : null}
                </StyledRadioButtonContainer>
            )}
        </>
    )
}

export default RadioButton
